import { EventTimeline, MatrixClient, Room } from 'matrix-js-sdk/src/matrix';

import { RoleAssignment, WorkspaceRoleV1, WorkspaceRoleV2 } from 'modules/CreateWorkspaceModal/types';
import { isEmailFormat } from 'modules/CreateWorkspaceModal/WorkspaceInviteStep/WorkspaceInviteForm/utils';
import { assignmentMatchesInvitee } from 'modules/Workspaces/WorkspaceSettingsModal/MembersTab/utils';
import { areValidAssignments, areValidRoles } from 'modules/Workspaces/WorkspacesProvider/useWorkspaceRoles';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import { WORKSPACE_ROLES_EVENT_VERSION } from 'utils/workspaceRolesEvent';

export type LiveWorkspaceRolesState = {
  roles: (WorkspaceRoleV1 | WorkspaceRoleV2)[];
  assignments: RoleAssignment[];
};

export const getLiveWorkspaceRolesState = (
  matrixClient: MatrixClient,
  workspaceId: string
): LiveWorkspaceRolesState | null => {
  const workspaceRoom = matrixClient.getRoom(workspaceId);
  if (!workspaceRoom) return null;

  const state = workspaceRoom.getLiveTimeline().getState(EventTimeline.FORWARDS);
  const roleEvent = state?.getStateEvents(CustomMeedioMatrixEvents.WORKSPACE_ROLES)[0];
  if (!roleEvent) return null;

  const content = roleEvent.getContent();
  if (!areValidRoles(content.roles) || !areValidAssignments(content.assignments)) return null;

  return { roles: content.roles, assignments: content.assignments };
};

export const captureInviteeAssignmentSnapshots = (
  assignments: RoleAssignment[],
  invitees: string[]
): Map<string, RoleAssignment | null> => {
  const snapshots = new Map<string, RoleAssignment | null>();

  for (const invitee of invitees) {
    const existing = assignments.find((assignment) => assignmentMatchesInvitee(assignment, invitee));
    snapshots.set(invitee, existing ? { ...existing } : null);
  }

  return snapshots;
};

export const buildAssignmentForInvitee = (invitee: string, roleId: string, assignedBy: string): RoleAssignment => {
  if (isEmailFormat(invitee)) {
    return { matrixId: null, pendingEmail: invitee, roleId, assignedBy };
  }

  return { matrixId: invitee, roleId, assignedBy };
};

export const applyInviteeRoleAssignments = (
  assignments: RoleAssignment[],
  invitees: string[],
  roleId: string,
  assignedBy: string
): RoleAssignment[] => {
  const updated = [...assignments];

  for (const invitee of invitees) {
    const index = updated.findIndex((assignment) => assignmentMatchesInvitee(assignment, invitee));
    const nextAssignment = buildAssignmentForInvitee(invitee, roleId, assignedBy);

    if (index >= 0) {
      updated[index] = { ...updated[index], ...nextAssignment };
    } else {
      updated.push(nextAssignment);
    }
  }

  return updated;
};

/** Restore pre-invite assignments for invitees whose Matrix invite failed. */
export const revertFailedInviteeAssignments = (
  assignments: RoleAssignment[],
  failedInvitees: string[],
  snapshots: Map<string, RoleAssignment | null>
): RoleAssignment[] => {
  let updated = [...assignments];

  for (const invitee of failedInvitees) {
    if (!snapshots.has(invitee)) continue;

    const previous = snapshots.get(invitee);
    const index = updated.findIndex((assignment) => assignmentMatchesInvitee(assignment, invitee));

    if (previous === null || previous === undefined) {
      if (index >= 0) {
        updated = updated.filter((_, assignmentIndex) => assignmentIndex !== index);
      }
      continue;
    }

    if (index >= 0) {
      updated[index] = { ...previous };
    } else {
      updated.push({ ...previous });
    }
  }

  return updated;
};

export const sendWorkspaceRolesState = async (
  matrixClient: MatrixClient,
  workspaceId: string,
  roles: LiveWorkspaceRolesState['roles'],
  assignments: RoleAssignment[]
): Promise<void> => {
  await matrixClient.sendStateEvent(workspaceId, CustomMeedioMatrixEvents.WORKSPACE_ROLES, {
    version: WORKSPACE_ROLES_EVENT_VERSION,
    roles,
    assignments,
  });
};

export const canSendWorkspaceRolesEvent = (workspaceRoom: Room | null, userId: string | null): boolean => {
  if (!workspaceRoom || !userId) return false;

  const state = workspaceRoom.getLiveTimeline().getState(EventTimeline.FORWARDS);
  return state?.maySendStateEvent(CustomMeedioMatrixEvents.WORKSPACE_ROLES, userId) ?? false;
};
